import { useState, useRef, useCallback } from "react";

interface PullToRefreshOptions {
  onRefresh: () => Promise<void>;
  threshold?: number;
  maxPull?: number;
}

interface PullToRefreshResult {
  containerRef: React.RefObject<HTMLDivElement>;
  isRefreshing: boolean;
  pullProgress: number;
  pullIndicatorStyle: Record<string, string | number>;
  handlers: {
    onTouchStart: (e: React.TouchEvent) => void;
    onTouchMove: (e: React.TouchEvent) => void;
    onTouchEnd: () => void;
  };
}

export function usePullToRefresh({
  onRefresh,
  threshold = 60,
  maxPull = 100,
}: PullToRefreshOptions): PullToRefreshResult {
  const containerRef = useRef<HTMLDivElement>(null!);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const startY = useRef(0);
  const isPulling = useRef(false);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    if (isRefreshing) return;
    const el = containerRef.current;
    if (!el || el.scrollTop > 0) return;
    startY.current = e.touches[0].clientY;
    isPulling.current = true;
  }, [isRefreshing]);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isPulling.current || isRefreshing) return;
    if (e.touches.length > 1) return;
    const el = containerRef.current;
    if (!el || el.scrollTop > 0) {
      isPulling.current = false;
      setPullDistance(0);
      return;
    }
    const dy = e.touches[0].clientY - startY.current;
    if (dy < 0) {
      setPullDistance(0);
      return;
    }
    if (dy > 5 && e.cancelable) {
      e.preventDefault();
    }
    const dampened = Math.min(dy * 0.5, maxPull);
    setPullDistance(dampened);
  }, [isRefreshing, maxPull]);

  const onTouchEnd = useCallback(async () => {
    if (!isPulling.current) return;
    isPulling.current = false;
    if (pullDistance >= threshold && !isRefreshing) {
      setIsRefreshing(true);
      setPullDistance(threshold * 0.6);
      try {
        await onRefresh();
      } finally {
        setIsRefreshing(false);
        setPullDistance(0);
      }
    } else {
      setPullDistance(0);
    }
  }, [pullDistance, threshold, isRefreshing, onRefresh]);

  const pullProgress = Math.min(pullDistance / threshold, 1);

  const pullIndicatorStyle: Record<string, string | number> = {
    height: `${pullDistance}px`,
    transition: isPulling.current ? "none" : "height 0.3s ease",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  return {
    containerRef,
    isRefreshing,
    pullProgress,
    pullIndicatorStyle,
    handlers: { onTouchStart, onTouchMove, onTouchEnd },
  };
}
